'use client';

import Cookies from 'js-cookie';
import Script from 'next/script';
import type OsanoConsentObject from '@alltrails/types/OsanoConsentObject';
import { getFacebookConsentValue } from '@alltrails/marketing-tracking/utils/marketingTrackingPixels/facebook';

const getConsentAction = (): 'grant' | 'revoke' => {
  // Can't default to 'revoke' as suggested by Meta. This usually runs after Osano is initialized.
  // https://alltrails.atlassian.net/browse/SEOMKT-555
  if (typeof window === 'undefined') {
    return 'revoke';
  }
  try {
    const atOsanoCookie = Cookies.get('at_osano');
    if (atOsanoCookie) {
      const parsed = JSON.parse(atOsanoCookie);
      const consentObj: OsanoConsentObject = parsed.consent;
      return getFacebookConsentValue(consentObj);
    }
  } catch {
    // Cookie missing or unparseable. Default to revoke
  }
  return 'revoke';
};

/**
 * Builds the argument string for the `fbq('init', ...)` call.
 * Includes advanced matching parameters if available.
 * @param userId - The AllTrails user ID, mapped to `external_id`.
 * @param emailSha256 - SHA-256 hashed email, mapped to `em`.
 * @see https://developers.facebook.com/docs/meta-pixel/advanced/advanced-matching/
 */
const getInitArgs = (userId?: number, emailSha256?: string | null) => {
  const advancedMatchingParams = {
    ...(userId && { external_id: userId }),
    ...(emailSha256 && { em: emailSha256 })
  };
  if (Object.keys(advancedMatchingParams).length > 0) {
    return `'${process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID}', ${JSON.stringify(advancedMatchingParams)}`;
  }

  return `'${process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID}'`;
};

export default function FacebookPixel({ userId, emailSha256 }: { userId?: number; emailSha256?: string | null }) {
  const consentAction = getConsentAction();
  const initArgs = getInitArgs(userId, emailSha256);

  return (
    <>
      <Script id="facebook-pixel" strategy="afterInteractive">
        {`!function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');

    fbq('consent', '${consentAction}');
    fbq('set', 'autoConfig', false, '${process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID}');
    fbq('init', ${initArgs});
    fbq('track', 'PageView');`}
      </Script>
      <noscript>
        {/* eslint-disable-next-line jsx-a11y/alt-text, @next/next/no-img-element */}
        <img
          loading="lazy"
          height="1"
          width="1"
          style={{ display: 'none' }}
          src={`https://www.facebook.com/tr?id=${process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID}&ev=PageView&noscript=1`}
        />
      </noscript>
    </>
  );
}
